import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Mic, MicOff, User, Briefcase, Users, Send, AlertTriangle, CheckCircle, XCircle, RotateCcw, Volume2, Loader2, Square, Play } from 'lucide-react';
import { cn } from '@/lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const roleIcons = {
  passenger: <User className="w-6 h-6" />,
  supervisor: <Briefcase className="w-6 h-6" />,
  interviewer: <Users className="w-6 h-6" />
};

const roleColors = {
  passenger: 'bg-blue-100 border-blue-300 text-blue-800',
  supervisor: 'bg-purple-100 border-purple-300 text-purple-800',
  interviewer: 'bg-orange-100 border-orange-300 text-orange-800'
};

const roleTitles = {
  passenger: 'Yolcu',
  supervisor: 'Amir',
  interviewer: 'Mülakatçı'
};

export default function SpeakingPractice({ unitId, onComplete }) {
  const { user } = useContext(AuthContext);
  const [sessionId, setSessionId] = useState(null);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [finalResult, setFinalResult] = useState(null);
  
  // Ses kaydı state'leri
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [transcribing, setTranscribing] = useState(false);
  const [inputMode, setInputMode] = useState('text'); // 'text' veya 'voice'
  const [pronunciationFeedback, setPronunciationFeedback] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    startPractice();
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startPractice = async () => {
    setLoading(true);
    setMessages([]);
    setCompleted(false);
    setFinalResult(null);
    setPronunciationFeedback(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/kariyer/speaking/start`, {
        unit_id: unitId
      });
      
      setSessionId(response.data.session_id);
      setCurrentScenario({
        number: response.data.scenario_number,
        total: response.data.total_scenarios,
        role: response.data.role,
        situation: response.data.situation
      });
      
      if (response.data.message) {
        setMessages([{
          type: 'ai',
          role: response.data.role,
          text: response.data.message
        }]);
      }
    } catch (error) {
      toast.error('Konuşma pratiği başlatılamadı');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Ses kaydı başlat
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(audioBlob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Kayıt süresini takip et
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      toast.error('Mikrofon erişimi sağlanamadı');
      console.error(error);
    }
  };

  // Ses kaydı durdur
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
  };

  // Ses kaydını gönder ve değerlendir
  const submitVoiceResponse = async () => {
    if (!audioBlob || !sessionId) return;
    
    setTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('session_id', sessionId);
      
      const response = await axios.post(
        `${BACKEND_URL}/api/kariyer/speaking/voice-evaluate`,
        formData,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true 
        }
      );
      
      const data = response.data;
      
      // Transkripsiyonu göster
      if (data.transcribed_text) {
        setMessages(prev => [...prev, {
          type: 'user',
          text: data.transcribed_text,
          isVoice: true
        }]);
      }
      
      // Telaffuz geri bildirimi
      if (data.pronunciation) {
        setPronunciationFeedback(data.pronunciation);
      }
      
      // Değerlendirme
      const evalData = data.evaluation;
      
      if (evalData?.evaluation) {
        setEvaluation(evalData.evaluation);
        setRetryCount(evalData.retry_count || 0);
      }
      
      if (evalData?.completed) {
        setCompleted(true);
        setFinalResult(evalData);
        if (evalData.passed && onComplete) {
          onComplete(evalData);
        }
        return;
      }
      
      if (evalData?.failed) {
        setCompleted(true);
        setFinalResult({ ...evalData, passed: false });
        return;
      }
      
      if (evalData?.message) {
        setMessages(prev => [...prev, {
          type: 'ai',
          role: currentScenario?.role,
          text: evalData.message,
          evaluation: evalData.evaluation
        }]);
      }
      
      if (evalData?.scenario_number !== currentScenario?.number) {
        setCurrentScenario({
          number: evalData.scenario_number,
          total: evalData.total_scenarios,
          role: evalData.role || currentScenario?.role,
          situation: evalData.situation || currentScenario?.situation
        });
        setEvaluation(null);
        setPronunciationFeedback(null);
      }
      
      setAudioBlob(null);
      
    } catch (error) {
      toast.error('Ses değerlendirmesi başarısız');
      console.error(error);
    } finally {
      setTranscribing(false);
    }
  };

  // Metin cevabı gönder
  const submitResponse = async () => {
    if (!userInput.trim() || !sessionId) return;
    
    setLoading(true);
    const userMessage = userInput;
    setUserInput('');
    
    setMessages(prev => [...prev, {
      type: 'user',
      text: userMessage
    }]);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/kariyer/speaking/respond`, {
        session_id: sessionId,
        response: userMessage
      });

      const data = response.data;

      if (data.evaluation) {
        setEvaluation(data.evaluation);
        setRetryCount(data.retry_count || 0);
      }

      if (data.completed) {
        setCompleted(true);
        setFinalResult(data);
        if (data.passed && onComplete) {
          onComplete(data);
        }
        return;
      }

      if (data.failed) {
        setCompleted(true);
        setFinalResult({ ...data, passed: false });
        return;
      }

      if (data.message) {
        setMessages(prev => [...prev, {
          type: 'ai',
          role: currentScenario?.role,
          text: data.message,
          evaluation: data.evaluation
        }]);
      }

      if (data.scenario_number !== currentScenario?.number) {
        setCurrentScenario({
          number: data.scenario_number,
          total: data.total_scenarios,
          role: data.role || currentScenario?.role,
          situation: data.situation || currentScenario?.situation
        });
        setEvaluation(null);
      }

    } catch (error) {
      toast.error('Cevap gönderilemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitResponse();
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Tamamlanma ekranı
  if (completed && finalResult) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className={`rounded-2xl p-8 text-center ${finalResult.passed ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
          {finalResult.passed ? (
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
          ) : (
            <XCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
          )}
          
          <h2 className="text-2xl font-bold mb-2">
            {finalResult.passed ? 'Tebrikler!' : 'Başarısız'}
          </h2>
          
          <p className="text-gray-600 mb-6">{finalResult.message}</p>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="text-3xl font-bold text-blue-600">
                {finalResult.average_score?.toFixed(0) || 0}%
              </div>
              <div className="text-sm text-gray-500">Ortalama Puan</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="text-3xl font-bold text-purple-600">
                {finalResult.cabin_readiness_score || 0}
              </div>
              <div className="text-sm text-gray-500">Cabin Readiness</div>
            </div>
          </div>

          <div className="text-sm text-gray-500 mb-4">
            {finalResult.scenarios_completed}/{finalResult.total_scenarios} senaryo tamamlandı
          </div>

          {finalResult.passed ? (
            <button
              onClick={() => onComplete && onComplete(finalResult)}
              className="bg-green-500 text-white px-8 py-3 rounded-xl font-medium hover:bg-green-600"
            >
              Devam Et
            </button>
          ) : (
            <button
              onClick={startPractice}
              className="bg-blue-500 text-white px-8 py-3 rounded-xl font-medium hover:bg-blue-600 flex items-center gap-2 mx-auto"
            >
              <RotateCcw className="w-5 h-5" />
              Tekrar Dene
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-2xl p-4 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mic className="w-6 h-6" />
            <span className="font-semibold">AI Konuşma Pratiği</span>
          </div>
          {currentScenario && (
            <div className="text-sm bg-white/20 px-3 py-1 rounded-full">
              Senaryo {currentScenario.number}/{currentScenario.total}
            </div>
          )}
        </div>
      </div>

      {/* Scenario Info */}
      {currentScenario && (
        <div className={`flex items-center gap-3 p-4 border-2 ${roleColors[currentScenario.role] || 'bg-gray-100'}`}>
          {roleIcons[currentScenario.role]}
          <div>
            <div className="font-semibold">{roleTitles[currentScenario.role]}</div>
            <div className="text-sm opacity-80">{currentScenario.situation}</div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="bg-gray-50 p-4 h-80 overflow-y-auto space-y-4">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={cn(
              "max-w-[80%] rounded-2xl p-4",
              msg.type === 'user' 
                ? 'bg-blue-500 text-white rounded-br-sm' 
                : 'bg-white border shadow-sm rounded-bl-sm'
            )}>
              {msg.type === 'ai' && (
                <div className="text-xs text-gray-500 mb-1 font-medium">
                  {roleTitles[msg.role]}
                </div>
              )}
              <div className="flex items-start gap-2">
                {msg.isVoice && <Volume2 className="w-4 h-4 mt-1 flex-shrink-0" />}
                <p className={msg.type === 'user' ? '' : 'text-gray-800'}>{msg.text}</p>
              </div>
              {msg.evaluation && (
                <div className={`mt-2 pt-2 border-t ${msg.type === 'user' ? 'border-white/20' : 'border-gray-100'}`}>
                  <div className="text-xs font-medium">
                    Puan: {msg.evaluation.score}/100
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {(loading || transcribing) && (
          <div className="flex justify-start">
            <div className="bg-white border shadow-sm rounded-2xl rounded-bl-sm p-4">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
            </div>
          </div>
        )}
      </div>

      {/* Pronunciation Feedback */}
      {pronunciationFeedback && !pronunciationFeedback.error && (
        <div className="bg-indigo-50 border border-indigo-200 p-4 mx-4 mt-2 rounded-xl">
          <h4 className="font-semibold text-indigo-800 mb-2 flex items-center gap-2">
            <Volume2 className="w-4 h-4" />
            Konuşma Değerlendirmesi
          </h4>
          <div className="grid grid-cols-4 gap-2 text-center mb-2">
            <div>
              <div className="text-lg font-bold text-indigo-600">{pronunciationFeedback.grammar_score || '-'}</div>
              <div className="text-xs text-gray-500">Gramer</div>
            </div>
            <div>
              <div className="text-lg font-bold text-green-600">{pronunciationFeedback.vocabulary_score || '-'}</div>
              <div className="text-xs text-gray-500">Kelime</div>
            </div>
            <div>
              <div className="text-lg font-bold text-blue-600">{pronunciationFeedback.fluency_score || '-'}</div>
              <div className="text-xs text-gray-500">Akıcılık</div>
            </div>
            <div>
              <div className="text-lg font-bold text-purple-600">{pronunciationFeedback.professionalism_score || '-'}</div>
              <div className="text-xs text-gray-500">Profesyonellik</div>
            </div>
          </div>
          {pronunciationFeedback.feedback && (
            <p className="text-sm text-indigo-700">{pronunciationFeedback.feedback}</p>
          )}
        </div>
      )}

      {/* Evaluation */}
      {evaluation && (
        <div className={`mx-4 mt-2 p-3 rounded-xl ${
          evaluation.passed ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-center gap-2 mb-1">
            {evaluation.passed ? (
              <CheckCircle className="w-4 h-4 text-green-600" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-yellow-600" />
            )}
            <span className={`text-sm font-medium ${evaluation.passed ? 'text-green-800' : 'text-yellow-800'}`}>
              Puan: {evaluation.score}/100
            </span>
            {retryCount > 0 && !evaluation.passed && (
              <span className="text-xs text-gray-500">
                ({3 - retryCount} deneme kaldı)
              </span>
            )}
          </div>
          <p className={`text-sm ${evaluation.passed ? 'text-green-700' : 'text-yellow-700'}`}>
            {evaluation.feedback}
          </p>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 bg-white border-t rounded-b-2xl">
        {/* Mode Toggle */}
        <div className="flex gap-2 mb-3 justify-center">
          <button
            onClick={() => setInputMode('text')}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all",
              inputMode === 'text' 
                ? 'bg-blue-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            ✍️ Yazarak Cevapla
          </button>
          <button
            onClick={() => setInputMode('voice')}
            className={cn(
              "px-4 py-2 rounded-lg text-sm font-medium transition-all",
              inputMode === 'voice' 
                ? 'bg-purple-500 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            )}
          >
            🎤 Sesli Cevapla
          </button>
        </div>

        {inputMode === 'text' ? (
          <div className="flex gap-2">
            <textarea
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="İngilizce cevabınızı yazın..."
              className="flex-1 p-3 border rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              disabled={loading}
            />
            <button
              onClick={submitResponse}
              disabled={loading || !userInput.trim()}
              className="px-4 bg-blue-500 text-white rounded-xl hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="text-center">
            {!isRecording && !audioBlob && (
              <button
                onClick={startRecording}
                disabled={transcribing}
                className="w-20 h-20 bg-gradient-to-br from-red-400 to-red-600 text-white rounded-full flex items-center justify-center mx-auto shadow-lg hover:scale-105 transition-transform disabled:opacity-50"
              >
                <Mic className="w-8 h-8" />
              </button>
            )}
            
            {isRecording && (
              <div className="space-y-3">
                <div className="relative w-20 h-20 mx-auto">
                  <div className="absolute inset-0 bg-red-500 rounded-full animate-pulse" />
                  <button
                    onClick={stopRecording}
                    className="absolute inset-0 bg-red-600 text-white rounded-full flex items-center justify-center z-10"
                  >
                    <Square className="w-6 h-6" />
                  </button>
                </div>
                <p className="text-red-600 font-medium animate-pulse">
                  🔴 Kayıt yapılıyor... {formatTime(recordingTime)}
                </p>
              </div>
            )}
            
            {audioBlob && !isRecording && (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-3">
                  <audio src={URL.createObjectURL(audioBlob)} controls className="h-10" />
                </div>
                <div className="flex gap-2 justify-center">
                  <button
                    onClick={() => setAudioBlob(null)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Tekrar Kaydet
                  </button>
                  <button
                    onClick={submitVoiceResponse}
                    disabled={transcribing}
                    className="px-6 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                  >
                    {transcribing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Değerlendiriliyor...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Gönder
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
            
            <p className="text-xs text-gray-500 mt-3">
              Mikrofon butonuna basıp İngilizce konuşun
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
